import React, { useMemo, useState } from "react";
import { Button, Grid, MenuItem, Stack, TextField, Typography } from "@mui/material";

import { JsonListChips, SectionCard, StatusChip, formatDateTime, normalizeJsonObject, safeArray, toTitleCase } from "../coreNetworkUiShared";

const emptyForm = {
  capabilityType: "",
  capabilityGroup: "",
  equipmentText: "",
  constraintsText: "",
};

export default function CoreNetworkCapabilityPanel({
  capabilities = [],
  onCreateCapability,
  saving = false,
}) {
  const [form, setForm] = useState(emptyForm);

  const sortedCapabilities = useMemo(
    () => [...safeArray(capabilities)].sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)),
    [capabilities]
  );

  const setField = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleCreate = async () => {
    await onCreateCapability?.({
      capabilityType: form.capabilityType,
      capabilityGroup: form.capabilityGroup || null,
      equipmentJson: form.equipmentText ? { notes: form.equipmentText } : null,
      constraintsJson: form.constraintsText ? { notes: form.constraintsText } : null,
      isActive: true,
    });
    setForm(emptyForm);
  };

  return (
    <SectionCard title="Capabilities" subtitle="Skills, methods, equipment, and working constraints for this provider.">
      {sortedCapabilities.length ? (
        <Stack spacing={2.25} sx={{ mb: 3 }}>
          {sortedCapabilities.map((capability) => {
            const equipmentJson = normalizeJsonObject(capability.equipmentJson);
            const constraintsJson = normalizeJsonObject(capability.constraintsJson);
            return (
              <Grid container spacing={2} key={capability.id} sx={{ p: 2, borderRadius: 2, border: "1px solid #edf1f7" }}>
                <Grid item xs={12} md={4}>
                  <Stack spacing={1}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <StatusChip value={capability.isActive ? "active" : "inactive"} />
                      <Typography variant="subtitle2">{toTitleCase(capability.capabilityType)}</Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      Group: {toTitleCase(capability.capabilityGroup)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Added {formatDateTime(capability.createdAt)}
                    </Typography>
                  </Stack>
                </Grid>
                <Grid item xs={12} md={8}>
                  <Stack spacing={1}>
                    <Typography variant="body2"><strong>Equipment:</strong> {equipmentJson.notes || "-"}</Typography>
                    <Typography variant="body2"><strong>Constraints:</strong> {constraintsJson.notes || "-"}</Typography>
                    <JsonListChips values={constraintsJson.garmentTypes || []} emptyLabel="No garment constraints recorded" />
                  </Stack>
                </Grid>
              </Grid>
            );
          })}
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          No capabilities are registered for this member yet.
        </Typography>
      )}

      {onCreateCapability ? (
        <Stack spacing={2}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Add capability
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Capability type" value={form.capabilityType} onChange={setField("capabilityType")} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth select label="Capability group" value={form.capabilityGroup} onChange={setField("capabilityGroup")}>
                <MenuItem value="">None</MenuItem>
                {["decoration", "design", "logistics", "software"].map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Equipment / notes" value={form.equipmentText} onChange={setField("equipmentText")} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Constraints" value={form.constraintsText} onChange={setField("constraintsText")} helperText="Short note for quantities, materials, or garment limits." />
            </Grid>
          </Grid>
          <div>
            <Button variant="outlined" onClick={handleCreate} disabled={saving || !form.capabilityType.trim()}>
              {saving ? "Saving..." : "Add capability"}
            </Button>
          </div>
        </Stack>
      ) : null}
    </SectionCard>
  );
}
